import {
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import { useForm } from 'react-hook-form';

import { TwitterAccount, twitterApi } from 'apis/twitter';
import { HookFormQueuesSelect, HookFormText } from 'components/hookForm';
// eslint-disable-next-line no-restricted-imports
import { ToggleBox, ToggleBoxHead } from 'fable/toggleBox';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

type TwitterDetailsFormProps = {
  twitterData: TwitterAccount;
  onCloseModal: VoidFunction;
  view: 'add' | 'edit';
};

type FieldValues = {
  name: string;
  queueId: string[];
  mentions: boolean;
};

const formId = 'twitter-details-form';

export const TwitterDetails = ({
  onCloseModal,
  twitterData,
  view,
}: TwitterDetailsFormProps): JSX.Element => {
  const notify = useNotifications();

  const [updateAccount, { isLoading }] = twitterApi.useUpdateAccountMutation();

  const form = useForm<FieldValues>({
    mode: 'all',
    defaultValues: {
      name: view === 'edit' ? twitterData.alias_name : twitterData.screen_name,
      queueId: [twitterData.queue_id],
      mentions: twitterData.tweets_enabled,
    },
  });

  const handleFormSubmit = async (values: FieldValues) => {
    try {
      await updateAccount({
        account_id: twitterData.account_id,
        alias_name: values.name,
        queue_id: values.queueId[0],
        tweets_enabled: values.mentions,
      });

      notify(
        {
          title:
            view === 'add'
              ? ' Twitter account successfully connected'
              : 'Twitter details successfully updated',
          time: 3000,
        },
        NotificationType.Success,
      );

      onCloseModal();
    } catch {
      notify(
        { title: 'An error occurred when trying to update Twitter details.' },
        NotificationType.Error,
      );
    }
  };

  return (
    <>
      <ModalHead
        title={`${view === 'add' ? 'Add' : 'Edit'} Twitter account @${
          twitterData.screen_name
        }`}
        onClose={onCloseModal}
      />

      <ModalBody>
        <form
          id={formId}
          noValidate
          tabIndex={-1}
          onSubmit={form.handleSubmit(handleFormSubmit)}
        >
          <FormGrid>
            <FormField>
              <HookFormText
                name="name"
                label="Name"
                control={form.control}
                rules={{
                  required: 'Name is required',
                }}
                componentProps={{
                  autoFocus: true,
                }}
              />
            </FormField>

            <FormField>
              <HookFormQueuesSelect
                name="queueId"
                label="Queue for incoming conversations"
                control={form.control}
                rules={{
                  required: 'Queue is required',
                }}
              />
            </FormField>

            <FormField>
              <FieldWrapper label="Create conversations for">
                <FlexBox flexDirection="column" gap="200">
                  <ToggleBox
                    title="Direct messages"
                    isOpen={true}
                    headComponent={ToggleBoxHead}
                    textProps={{ variant: '2' }}
                    input={{
                      name: 'directMessages',
                      value: '',
                    }}
                    helpIcon={{
                      dark: true,
                      text: 'Direct messages are the most common type of conversations. A conversation is created when someone sends you a direct message.',
                    }}
                    disabled
                  />

                  <ToggleBox
                    title="Mentions"
                    isOpen={form.watch('mentions')}
                    headComponent={ToggleBoxHead}
                    textProps={{ variant: '2' }}
                    input={{
                      name: 'mentions',
                      value: '',
                    }}
                    helpIcon={{
                      dark: true,
                      text: 'Conversations for mentions are created when someone mentions you in a tweet.',
                    }}
                    onChange={() =>
                      form.setValue('mentions', !form.watch('mentions'))
                    }
                  />
                </FlexBox>
              </FieldWrapper>
            </FormField>
          </FormGrid>
        </form>
      </ModalBody>

      <ModalFoot
        cancelOnClick={onCloseModal}
        confirmName="Save"
        confirmOnClick={noop}
        confirmProps={{
          type: 'submit',
          isLoading,
          disabled: !form.formState.isValid || form.formState.isSubmitting,
          form: formId,
        }}
      />
    </>
  );
};
